import {
  memo,
  orderColumns,
  functionalUpdate,
  type Column as ReactTableColumn,
  type ColumnOrderState,
} from '@tanstack/react-table';
import { isEqual } from 'lodash-es';
import { useCallback, useRef } from 'react';

import {
  extractColumnOrderAndGroups,
  validateColumnOrder,
} from './column-order-validation.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import type {
  Column,
  Header,
  RowData,
  Table,
  TableFeature,
  TableOptions,
  Updater,
} from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type {
  DataTableV2ColumnDef,
  DataTableV2RowData,
} from '../../public.api.js';

/**
 * @public
 */
export interface DataTableV2ColumnOrderBaseProps {
  /**
   * Enables column ordering functionality in the table.
   */
  columnOrdering?: boolean;
  /**
   * Callback that is called when the column order of any column is changed.
   * It provides the changed column order along with the underlying trigger that indicates
   * if the change was triggered by a user interaction or internally.
   */
  onColumnOrderChange?: (
    columnOrder: string[],
    trigger: 'user' | 'internal',
  ) => void;
}
/**
 * @public
 */
export interface DataTableV2ColumnOrderControlledProps
  extends DataTableV2ColumnOrderBaseProps {
  /**
   * Lets you control the column order of the individual columns.
   */
  columnOrder?: string[];

  /**
   * Lets you set the initial column order of the individual columns.
   */
  defaultColumnOrder?: never;
}
/**
 * @public
 */
export interface DataTableV2ColumnOrderUncontrolledProps
  extends DataTableV2ColumnOrderBaseProps {
  /**
   * Lets you control the column order of the individual columns.
   */
  columnOrder?: never;

  /**
   * Lets you set the initial column order of the individual columns.
   */
  defaultColumnOrder?: string[];
}

/**
 * Interface defining the props on the DataTableV2 that control the column order feature.
 * @public
 */
export type DataTableV2ColumnOrderProps =
  | DataTableV2ColumnOrderControlledProps
  | DataTableV2ColumnOrderUncontrolledProps;

/**
 * Extension interface to extend the tanstack table column order state.
 * @internal
 */
export interface DataTableV2ColumnOrderOptions {
  /**
   * The initial column order.
   */
  originalColumnOrder?: string[];
  /**
   * Enables column ordering.
   */
  enableColumnOrdering?: boolean;
}

/** @internal */
export interface DataTableV2ColumnOrderState {
  /**
   * The id of the currently dragged header.
   */
  draggedHeaderColumn?: Column<unknown, unknown>;
}

/**
 * @internal
 */
export interface DataTableV2ColumnOrderInstance<
  TData extends DataTableV2RowData,
> {
  _getOrderColumnsFn: () => (
    columns: Column<TData, unknown>[],
  ) => Column<TData, unknown>[];
  /**
   * Returns an array of all leaf-node columns without the builtin prefix/suffix columns.
   */
  getLeafColumnsWithoutBuiltin: () => Column<TData, unknown>[];
  /**
   * Sets or updates the `state.draggedHeaderId` state.
   */
  setDraggedHeaderColumn: (column?: Column<unknown, unknown>) => void;
}

/**
 * Extension interface to extend the tanstack table header instance.
 * @internal
 */
export interface DataTableV2ColumnOrderHeader {
  /**
   * Defines the drag and drop interaction mode for the current header.
   * - 'none': Drag and drop is disabled.
   * - 'enabled': Drag and drop is enabled along with other interactions.
   * - 'only': Drag and drop is the only interaction (no menu/sorting).
   */
  getColumnDragAndDropMode: () => 'none' | 'enabled' | 'only';
  /**
   * Whether drag and drop is disabled on the header (if a child column is dragged).
   */
  getIsDragAndDropDisabled: () => boolean;
}

/**
 * @internal
 */
export interface DataTableV2ColumnOrderColumn<
  TData extends DataTableV2RowData,
> {
  /**
   * Returns whether a column can be moved to the left. If the column is the first column overall or the first column in a group this function returns `false`.
   * For usage in the column settings modal, it is possible to pass local columns that should be considered, instead of the ones from the current table state.
   * @param localLeafColumns - ordered leaf columns to compare with, including hidden ones
   * @param localSiblings - ordered sibling columns to compare with, including hidden ones
   */
  getCanMoveColumnLeft: (
    localLeafColumns?: Column<TData, unknown>[],
    localSibilings?: Column<TData, unknown>[],
  ) => boolean;
  /**
   * Returns whether a column can be moved to the right. If the column is the last column overall or the last column in a group this function returns `false`.
   * For usage in the column settings modal, it is possible to pass local columns that should be considered, instead of the ones from the current table state.
   * @param localLeafColumns - ordered leaf columns to compare with, including hidden ones
   * @param localSiblings - ordered sibling columns to compare with, including hidden ones
   */
  getCanMoveColumnRight: (
    localLeafColumns?: Column<TData, unknown>[],
    localSibilings?: Column<TData, unknown>[],
  ) => boolean;
  /**
   * Moves a column to the left and returns the updated column order.
   * For usage in the column settings modal, it is possible to pass the ordered local columns and column order that should be considered, instead of using the current table state.
   * The table state is only updated if no local columns are passed.
   * @param localLeafColumns - ordered leaf columns to compare with, including hidden ones
   * @param localColumnOrder - the initial column order that should be adjusted
   */
  moveColumnLeft: (
    localLeafColumns?: Column<TData, unknown>[],
    localColumnOrder?: string[],
  ) => string[];
  /**
   * Moves a column to the right and returns the updated column order.
   * For usage in the column settings modal, it is possible to pass the ordered local columns and column order that should be considered, instead of using the current table state.
   * The table state is only updated if no local columns are passed.
   * @param localLeafColumns - ordered leaf columns to compare with, including hidden ones
   * @param localColumnOrder - the initial column order that should be adjusted
   */
  moveColumnRight: (
    localLeafColumns?: Column<TData, unknown>[],
    localColumnOrder?: string[],
  ) => string[];
}

/**
 * Additional feature implementation for column order.
 * @internal
 */
export const DataTableV2ColumnOrder: TableFeature = {
  createTable<TData extends DataTableV2RowData>(table: Table<TData>): void {
    // overwrite the order columns fn from react table so that builtin columns are placed correctly
    table._getOrderColumnsFn = memo(
      () => [
        table.getState().columnOrder,
        table.getState().grouping,
        table.options.groupedColumnMode,
      ],
      (columnOrder, grouping, groupedColumnMode) =>
        (columns: Column<TData, unknown>[]) => {
          const prefixColumns: Column<TData, unknown>[] = [];
          const suffixColumns: Column<TData, unknown>[] = [];
          let orderedColumns: Column<TData, unknown>[] = columns;

          // If there is no order, return the normal columns
          if (columnOrder.length) {
            const columnsMap = new Map();

            columns.forEach((column) => {
              if (column.columnDef.meta?.builtinPlacement === 'prefix') {
                prefixColumns.push(column);
              } else if (column.columnDef.meta?.builtinPlacement === 'suffix') {
                suffixColumns.push(column);
              } else {
                columnsMap.set(column.id, column);
              }
            });

            orderedColumns = [
              ...prefixColumns,
              ...columnOrder.flatMap((id) => columnsMap.get(id) ?? []),
              ...suffixColumns,
            ];
          }

          return orderColumns(
            orderedColumns as ReactTableColumn<TData>[],
            grouping,
            groupedColumnMode,
          ) as Column<TData, unknown>[];
        },
      getMemoOptions(table.options, 'debugCells', 'cell._getOrderColumnsFn'),
    );

    // Helper function that provides an array of all leaf-node columns, excluding builtin columns.
    table.getLeafColumnsWithoutBuiltin = () => {
      return table
        .getAllLeafColumns()
        .filter((leafColumn) => !leafColumn.columnDef.meta?.builtin);
    };

    table.setDraggedHeaderColumn = (column) => {
      table.setState((state) => ({
        ...state,
        draggedHeaderColumn: column,
      }));
    };
  },

  createHeader<TData extends RowData, TValue>(
    header: Header<TData, TValue>,
    table: Table<TData>,
  ) {
    header.getColumnDragAndDropMode = () => {
      if (
        header.column.columnDef.enableUserActions === false ||
        table.options.enableColumnOrdering !== true
      ) {
        return 'none';
      }
      if (
        !header.column.getCanUserActions() &&
        !header.getCanSortByHeaderClick()
      ) {
        return 'only';
      }
      return 'enabled';
    };

    header.getIsDragAndDropDisabled = () => {
      const draggedHeaderParentId =
        table.getState().draggedHeaderColumn?.parent?.id;

      if (
        draggedHeaderParentId === undefined ||
        draggedHeaderParentId === header.column.id ||
        draggedHeaderParentId === header.column.parent?.id
      ) {
        return false;
      } else {
        return true;
      }
    };
  },

  createColumn<TData extends DataTableV2RowData, TValue>(
    column: Column<TData, TValue>,
    table: Table<TData>,
  ) {
    column.getCanMoveColumnLeft = (
      localLeafColumns?: Column<TData, unknown>[],
      localSibilings?: Column<TData, unknown>[],
    ) => {
      let columnsToCompare: Column<TData, unknown>[] = [];
      let columnId: string | undefined = column.id;

      if (column.columns.length > 0) {
        columnsToCompare =
          localLeafColumns || table.getVisibleLeafColumnsWithoutBuiltin();
        columnId = columnsToCompare.find(
          (leafColumn) =>
            leafColumn.id === column.id || leafColumn.parent?.id === column.id,
        )?.id;
      } else if (column.parent) {
        columnsToCompare =
          localSibilings || column.parent.getVisibleLeafColumns();
      } else {
        columnsToCompare =
          localLeafColumns || table.getVisibleLeafColumnsWithoutBuiltin();
      }

      return columnsToCompare.at(0)?.id !== columnId;
    };

    column.getCanMoveColumnRight = (
      localLeafColumns?: Column<TData, unknown>[],
      localSiblings?: Column<TData, unknown>[],
    ) => {
      let columnsToCompare: Column<TData, unknown>[] = [];
      let columnId: string | undefined = column.id;

      if (column.columns.length > 0) {
        columnsToCompare =
          localLeafColumns || table.getVisibleLeafColumnsWithoutBuiltin();
        columnId = [...columnsToCompare]
          .reverse()
          .find(
            (leafColumn) =>
              leafColumn.id === column.id ||
              leafColumn.parent?.id === column.id,
          )?.id;
      } else if (column.parent) {
        columnsToCompare =
          localSiblings || column.parent.getVisibleLeafColumns();
      } else {
        columnsToCompare =
          localLeafColumns || table.getVisibleLeafColumnsWithoutBuiltin();
      }

      return columnsToCompare.at(-1)?.id !== columnId;
    };

    column.moveColumnLeft = (
      localLeafColumns?: Column<TData, unknown>[],
      localColumnOrder?: string[],
    ) => {
      const allLeafColumns =
        localLeafColumns || table.getLeafColumnsWithoutBuiltin();
      const currentColumnOrder =
        localColumnOrder || table.getState().columnOrder;
      const columnOrder = [
        ...(currentColumnOrder || allLeafColumns.map((col) => col.id)),
      ];

      const childColumnsLength = column.columns.length;
      const moveCount = childColumnsLength || 1;
      const startIndex = allLeafColumns.findIndex(
        (leafColumn) =>
          leafColumn.id === column.id || leafColumn.parent?.id === column.id,
      );

      let targetIndex = startIndex - 1;
      // we need to adjust the target index in case the target column is hidden, if it is a column group
      while (targetIndex >= 0) {
        let targetColumn = allLeafColumns[targetIndex];
        // if the target column would be a group, adjust the target index to be before/after the group
        if (!column.parent && targetColumn.parent) {
          targetIndex = targetIndex - (targetColumn.parent.columns.length - 1);
          targetColumn = allLeafColumns[targetIndex];
        }
        const isTargetColumnVisible = targetColumn?.parent
          ? targetColumn.parent.getIsVisible()
          : targetColumn?.getIsVisible();
        // if the target column (group) is not visible, move to the next column
        if (!localLeafColumns && !isTargetColumnVisible) {
          targetIndex--;
        } else {
          break;
        }
      }

      // Extract the column(s) that should be moved
      const columnsToMove = columnOrder.splice(startIndex, moveCount);
      // Insert the column(s) at the new position
      columnOrder.splice(targetIndex, 0, ...columnsToMove);

      if (!localLeafColumns) {
        table.setColumnOrder(columnOrder);
      }

      return columnOrder;
    };

    column.moveColumnRight = (
      localLeafColumns?: Column<TData, unknown>[],
      localColumnOrder?: string[],
    ) => {
      const allLeafColumns =
        localLeafColumns || table.getLeafColumnsWithoutBuiltin();
      const currentColumnOrder =
        localColumnOrder || table.getState().columnOrder;
      const columnOrder = [
        ...(currentColumnOrder || allLeafColumns.map((col) => col.id)),
      ];

      const childColumnsLength = column.columns.length;
      const moveCount = childColumnsLength || 1;
      const startIndex = allLeafColumns.findIndex(
        (leafColumn) =>
          leafColumn.id === column.id || leafColumn.parent?.id === column.id,
      );

      let targetIndex = startIndex + 1;
      // we need to adjust the target index in case the target column is hidden, if it is a column group
      while (targetIndex <= columnOrder.length - moveCount) {
        let targetColumn = allLeafColumns[targetIndex + moveCount - 1];
        // if the target column would be a group, adjust the target index to be before/after the group
        if (!column.parent && targetColumn.parent) {
          targetIndex = targetIndex + (targetColumn.parent.columns.length - 1);
          targetColumn = allLeafColumns[targetIndex + moveCount - 1];
        }
        const isTargetColumnVisible = targetColumn?.parent
          ? targetColumn.parent?.getIsVisible()
          : targetColumn?.getIsVisible();
        // if the target column is not visible, move to the next column
        if (!localLeafColumns && !isTargetColumnVisible) {
          targetIndex++;
        } else {
          break;
        }
      }

      // Extract the column(s) that should be moved
      const columnsToMove = columnOrder.splice(startIndex, moveCount);
      // Insert the column(s) at the new position
      columnOrder.splice(targetIndex, 0, ...columnsToMove);

      if (!localLeafColumns) {
        table.setColumnOrder(columnOrder);
      }

      return columnOrder;
    };
  },
};

/**
 * Configuration hook for the DataTableV2 ColumnOrder feature.
 * @internal
 */
export function useColumnOrder<TData extends DataTableV2RowData>(
  props: DataTableV2ColumnOrderProps & {
    columns: DataTableV2ColumnDef<TData>[];
  },
  options: TableOptions<TData>,
) {
  options.enableColumnOrdering = false;

  const { columnOrder: columnOrderFromDefinition, columnGroups } =
    extractColumnOrderAndGroups<TData>(props.columns);

  // save the last valid column order to be able to check the validity of a new one
  const lastValidColumnOrder = useRef<string[] | undefined>(
    columnOrderFromDefinition,
  );
  // save the column order inferred from the column definition to be able to check if it changed
  const lastColumnOrderFromDefinition = useRef<string[] | undefined>(
    columnOrderFromDefinition,
  );

  const [columnOrder, setColumnOrder] = useControllableState({
    value: props.columnOrder,
    // if no order was specified, initialize with column definition order
    defaultValue: props.defaultColumnOrder || columnOrderFromDefinition,
  });

  const onColumnOrderChangeCallback = useCallback(
    (
      updater: Updater<ColumnOrderState>,
      trigger: 'user' | 'internal' = 'user',
    ) => {
      const nextState = functionalUpdate(
        updater,
        options.state?.columnOrder ?? columnOrder,
      );

      if (!isEqual(nextState, columnOrder)) {
        setColumnOrder(nextState);
        props.onColumnOrderChange?.(nextState, trigger);
      }
    },
    [columnOrder, options.state?.columnOrder, props, setColumnOrder],
  );

  if (!props.columnOrdering) {
    return;
  }

  // uncontrolled, column definition changed - update
  if (
    props.columnOrder === undefined &&
    !isEqual(lastColumnOrderFromDefinition.current, columnOrderFromDefinition)
  ) {
    onColumnOrderChangeCallback(columnOrderFromDefinition, 'internal');
    lastColumnOrderFromDefinition.current = columnOrderFromDefinition;
  } else if (!isEqual(columnOrder, lastValidColumnOrder.current)) {
    // Validate the column order when the current order is not equal
    // with the last valid order or if the current order has different
    // elements than the order extracted from the column definition.
    const validColumnOrder = validateColumnOrder(
      columnOrder,
      columnOrderFromDefinition,
      columnGroups,
    );
    // only update column order if it was invalid
    if (validColumnOrder !== null) {
      onColumnOrderChangeCallback(validColumnOrder, 'internal');
      lastValidColumnOrder.current = validColumnOrder;
    }
  }

  options.enableColumnOrdering = true;
  options.state = { ...options.state, columnOrder };

  options.onColumnOrderChange = onColumnOrderChangeCallback;

  options.originalColumnOrder =
    props.defaultColumnOrder ?? lastValidColumnOrder.current;
}
